/**
 * Minimal cortex-owned client for the two Formant admin-API calls the console
 * needs to run a Coherence session — deliberately dependency-free (no
 * `@formant/*`) so the console doesn't inherit the suite's heavy client/UI
 * libraries. Contract mirrors what `@formant/core`'s AuthClient/UserClient do
 * on the wire:
 *   - refresh:  POST {base}/v1/admin/auth/refresh   { refreshToken }
 *   - userinfo: GET  {base}/v1/admin/users/{userId} (Bearer)
 */

/** Result of exchanging a refresh token for an access token. */
export interface IFormantSession {
  accessToken: string;
  refreshToken: string;
  userId: string;
  organizationId: string;
}

/** Display fields for the signed-in user. */
export interface IFormantUserInfo {
  email: string | null;
  name: string | null;
}

export interface IFormantAuthClient {
  refresh(refreshToken: string): Promise<IFormantSession>;
  getUserInfo(userId: string, accessToken: string): Promise<IFormantUserInfo>;
  getOrgName(orgId: string, accessToken: string): Promise<string | null>;
}

export function createFormantAuthClient(
  apiBaseUrl: string,
  fetchImpl: typeof fetch = fetch,
): IFormantAuthClient {
  const adminBase = `${apiBaseUrl.replace(/\/$/, '')}/v1/admin`;

  return {
    async refresh(refreshToken: string): Promise<IFormantSession> {
      const res = await fetchImpl(`${adminBase}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        throw new Error(`Formant refresh failed: ${res.status}`);
      }
      const body = (await res.json()) as {
        authentication?: {
          accessToken?: unknown;
          refreshToken?: unknown;
          userId?: unknown;
          organizationId?: unknown;
        };
      };
      const a = body.authentication;
      if (
        !a ||
        typeof a.accessToken !== 'string' ||
        typeof a.refreshToken !== 'string' ||
        typeof a.userId !== 'string' ||
        typeof a.organizationId !== 'string'
      ) {
        throw new Error('Formant refresh returned a malformed response');
      }
      return {
        accessToken: a.accessToken,
        refreshToken: a.refreshToken,
        userId: a.userId,
        organizationId: a.organizationId,
      };
    },

    // Best-effort: display fields only. A failure here degrades to null rather
    // than breaking sign-in.
    async getUserInfo(userId: string, accessToken: string): Promise<IFormantUserInfo> {
      try {
        const res = await fetchImpl(`${adminBase}/users/${encodeURIComponent(userId)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return { email: null, name: null };
        const u = (await res.json()) as {
          email?: unknown;
          firstName?: unknown;
          lastName?: unknown;
        };
        const first = typeof u.firstName === 'string' ? u.firstName : '';
        const last = typeof u.lastName === 'string' ? u.lastName : '';
        const name = [first, last].filter(Boolean).join(' ') || null;
        return { email: typeof u.email === 'string' && u.email ? u.email : null, name };
      } catch {
        return { email: null, name: null };
      }
    },

    // Best-effort org display name. Null on any failure — never blocks sign-in.
    async getOrgName(orgId: string, accessToken: string): Promise<string | null> {
      try {
        const res = await fetchImpl(`${adminBase}/organizations/${encodeURIComponent(orgId)}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return null;
        const org = (await res.json()) as { name?: unknown };
        return typeof org.name === 'string' && org.name ? org.name : null;
      } catch {
        return null;
      }
    },
  };
}
